import { useEffect, useState, useRef } from "react";
import { focusElement } from "../js/utils";
import "../css/InputData.css";
import "../css/InputIconSearch.css";
import "../css/InputIconSearchPanel.css";
import TooltipBox from "./Tooltip";

export default function InputIconSearchPanel(props) {
  const [togglePanel, setTogglePanel] = useState(false);
  const [optionSelected, setOptionSelected] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  const getInputSize = () => {
    const sizeClass = {
      0: "input-full",
      1: "input-small",
      2: "input-medium",
      3: "input-large",
      4: "input-super-small",
    };
    return sizeClass[props.size] || "";
  };

  const searchOptions = () => {
    let optionListAux = props.options;
    optionListAux = optionListAux.filter((e) => e.nombre.includes(props.input));
    if (optionListAux.length > 0) {
      setTogglePanel(true);
      setHighlightedIndex(0);
    }
    if (props.input === optionSelected) {
      setTogglePanel(false);
    }
  };

  const getActiveFamilyIcon = (id) => {
    const iconObj = {
      0: "/images/preferenciaAudifonos.svg",
      1: "/images/preferenciaGafasGraduadas.svg",
      2: "/images/preferenciaLentillas.svg",
      3: "/images/preferenciaLiquido.svg",
      4: "/images/accommodative-blue.svg",
      5: "/images/preferenciaVarios.svg",
      6: "/images/preferenciaGafasDeSol.svg",
    };
    return iconObj[id] || "";
  };

  useEffect(() => {
    if (props.inactive) {
      setTogglePanel(false);
    }
  }, [props.inactive]);

  useEffect(() => {
    if (props.input !== "") {
      searchOptions();
    }
  }, [props.input]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (togglePanel) {
        if (e.key === "ArrowDown") {
          setHighlightedIndex((prevIndex) =>
            prevIndex < props.options.length - 1 ? prevIndex + 1 : 0
          );
        } else if (e.key === "ArrowUp") {
          setHighlightedIndex((prevIndex) =>
            prevIndex > 0 ? prevIndex - 1 : props.options.length - 1
          );
        } else if (e.key === "Enter") {
          if (highlightedIndex >= 0 && highlightedIndex < props.options.length) {
            const selectedOption = props.options[highlightedIndex];
            setOptionSelected(selectedOption.nombre);
            props.onSelect(selectedOption);
            props.onChange(selectedOption.nombre);
            setTogglePanel(false);
          }
        } else if (e.key === "Escape") {
          setTogglePanel(false);
        }
      }
    };

    const handleClickOutside = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !inputRef.current.contains(e.target)
      ) {
        setTogglePanel(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [togglePanel, highlightedIndex, props.options]);

  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      const highlightedOption = optionRefs.current[highlightedIndex];
      const panel = panelRef.current;
      const optionTop = highlightedOption.offsetTop;
      const optionBottom = optionTop + highlightedOption.clientHeight;
      const panelTop = panel.scrollTop;
      const panelBottom = panelTop + panel.clientHeight;

      if (optionTop < panelTop) {
        panel.scrollTop = optionTop;
      } else if (optionBottom > panelBottom) {
        panel.scrollTop = optionBottom - panel.clientHeight;
      }
    }
  }, [highlightedIndex]);

  return (
    <div className="input-search-panel-container">
      <div
        className={`input-data input-icon-search ${getInputSize()} ${
          props.inactive ? "input-search-inactive" : ""
        }`}
        ref={inputRef}
      >
        <img
          src="/images/search-blue.svg"
          loading="lazy"
          width="14"
          height="14"
          alt="icon"
          className="input-icon-search-img"
        />
        <input
          id={props.id}
          tabIndex={props.tabIndex}
          type={props.type ? props.type : "text"}
          disabled={props.inactive}
          placeholder={props.placeholder}
          onChange={(e) => {
            props.onChange(e.target.value.toUpperCase());
            setHighlightedIndex(0);
          }}
          onClick={() => {
            setTogglePanel(true);
          }}
          value={props.input}
        ></input>
        {props.input !== "" && (
          <img
            src="/images/close-dark.svg"
            loading="lazy"
            width="14"
            height="14"
            alt="icon"
            className="input-icon-search-clear"
            onClick={() => {
              props.onClear();
              setHighlightedIndex(-1);
              focusElement(props.tabIndex);
            }}
          />
        )}
        <img
          src="/images/list-blue.svg"
          loading="lazy"
          width="14"
          height="14"
          alt="icon"
          className="input-icon-search-clear"
          style={{ marginLeft: "10px" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!props.inactive) {
              setTogglePanel((prevToggle) => !prevToggle);
              setHighlightedIndex(-1);
              focusElement(props.tabIndex);
            }
          }}
        />
      </div>
      {togglePanel && (
        <div className="input-search-panel-ops" ref={panelRef}>
          {props.options.map((e, i) => {
            return (
              <div
                className={`search-result-info ${
                  props.input === e.nombre || highlightedIndex === i
                    ? "search-result-info-selected"
                    : ""
                }`}
                key={i}
                onClick={() => {
                  setOptionSelected(e.nombre);
                  props.onSelect(e);
                  props.onChange(e.nombre);
                  setTogglePanel(false);
                }}
                ref={(el) => (optionRefs.current[i] = el)}
              >
                {e.nombre}
                <div className="active-families-icon">
                  {e.familiasActivas.map((id, index) => {
                    return (
                      <img
                        src={getActiveFamilyIcon(id)}
                        loading="lazy"
                        alt="icon"
                        style={{ width: "12px" }}
                        key={index}
                      />
                    );
                  })}
                </div>
                <img
                  src="/images/eye-orange.svg"
                  loading="lazy"
                  alt="icon"
                  className="icon-pref"
                  data-tooltip-id={`tt${i}`}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                />
                <TooltipBox
                  id={`tt${i}`}
                  content={
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 5,
                      }}
                    >
                      <div>
                        <span className="opt-table-subtitle">
                          Nombre:&nbsp;
                        </span>
                        {e.nombre}
                      </div>
                      <div>
                        <span className="opt-table-subtitle">NIF:&nbsp;</span>
                        {e.nif}
                      </div>
                      <div>
                        <span className="opt-table-subtitle">
                          Código:&nbsp;
                        </span>
                        {e.codigo}
                      </div>
                      <div>
                        <span className="opt-table-subtitle">
                          Contacto:&nbsp;
                        </span>
                        {e.contacto}
                      </div>
                      <div>
                        <span className="opt-table-subtitle">Email:&nbsp;</span>
                        {e.email}
                      </div>
                      <div>
                        <span className="opt-table-subtitle">
                          Teléfono:&nbsp;
                        </span>
                        {e.telefono}
                      </div>
                      <div>
                        <span className="opt-table-subtitle">
                          Dirección:&nbsp;
                        </span>
                        {e.direccion}
                      </div>
                      <div>
                        <span className="opt-table-subtitle">
                          Localidad:&nbsp;
                        </span>
                        {e.localidad}
                      </div>
                      <div>
                        <span className="opt-table-subtitle">CP:&nbsp;</span>
                        {e.cp}
                      </div>
                      <div>
                        <span className="opt-table-subtitle">
                          Familias activas ({e.familiasActivas.length}
                          ):&nbsp;
                          {e.familiasActivas.map((id, index) => {
                            return (
                              <img
                                src={getActiveFamilyIcon(id)}
                                loading="lazy"
                                alt="icon"
                                style={{ width: "12px", marginRight: 5 }}
                                key={index}
                              />
                            );
                          })}
                        </span>
                      </div>
                    </div>
                  }
                ></TooltipBox>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
